import { useContext } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { chunk } from 'lodash';

import { ConfigContext } from '../../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../../aurr/contexts/LoginContext';
import { buildCacheKey, getAxiosInstance } from '../../../axiosCache';
import {
  REPLIES_MUTATE_POST_KEY,
  REPLIES_QUERY_KEY,
  TOPIC_QUERY_KEY,
  USER_QUERY_KEY,
} from '../../constants';
import { KhorosRepliesProps } from '../../interface/khoros.interface';
import { KhorosRepliesGetSchema } from '../../type/khoros-replies.type';
import { KhorosTopicSchema } from '../../type/khoros-topic.type';
import { KhorosUserSchema } from '../../type/khoros-user.type';
import { useKhorosUser } from '../useKhorosUser';

export const useKhorosReply = ({
  topicId,
  baseURL,
  commentId = '',
  pageSize,
}: KhorosRepliesProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  const { access_token, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  const axiosConfig: CacheRequestConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
    cache: {
      update: {
        [buildCacheKey([REPLIES_QUERY_KEY, 'reply', commentId])]: 'delete',
        [buildCacheKey([REPLIES_QUERY_KEY, topicId])]: 'delete',
        [buildCacheKey([USER_QUERY_KEY, sub])]: 'delete',
        [buildCacheKey([TOPIC_QUERY_KEY, topicId, sub])]: 'delete',
      },
    },
  };

  const { data: userResponse } = useKhorosUser({
    baseURL,
  });

  const api = getAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [REPLIES_MUTATE_POST_KEY, { commentId, sub }],
    mutationFn: async body => {
      const response = await api.post(
        `/services/v2/community/khoros/replies?parent_id=${commentId}`,
        {
          body,
        },
        axiosConfig
      );
      return response?.data || undefined;
    },
    onMutate: async (body: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REPLIES_QUERY_KEY, { topicId }],
      });
      await queryClient.cancelQueries({
        queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
      });
      await queryClient.cancelQueries({
        queryKey: [REPLIES_QUERY_KEY, { commentId }],
      });
      await queryClient.cancelQueries({ queryKey: [USER_QUERY_KEY, { sub }] });

      const savedTopic = queryClient.getQueryData([
        TOPIC_QUERY_KEY,
        { topicId, sub },
      ]);

      const savedReplies = queryClient.getQueryData([
        REPLIES_QUERY_KEY,
        { topicId },
      ]);

      // Snapshot the previous value
      const savedReply = queryClient.getQueryData([
        REPLIES_QUERY_KEY,
        { commentId, sub },
      ]);

      const savedUser = queryClient.getQueryData([USER_QUERY_KEY, { sub }]);

      // Optimistically update to the new value
      queryClient.setQueryData<KhorosTopicSchema>(
        [TOPIC_QUERY_KEY, { topicId, sub }],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            kh_total_replies_count: old.kh_total_replies_count + 1,
          };
        }
      );

      queryClient.setQueryData<InfiniteData<KhorosRepliesGetSchema>>(
        [REPLIES_QUERY_KEY, { topicId }],
        old => {
          const newResponse = {
            pages: [...(old?.pages || [])],
            pageParams: old?.pageParams || [{ 0: undefined }],
          };

          let commentIndex = -1;
          const pageIndex = newResponse.pages.findIndex(page => {
            const foundCommentIndex = page.kh_replies.findIndex(
              obj => obj.kh_post_id === commentId
            );
            if (foundCommentIndex > -1) {
              commentIndex = foundCommentIndex;
              return true;
            }
            return false;
          });

          if (pageIndex === -1 || commentIndex === -1) {
            // eslint-disable-next-line no-console
            console.error(
              '[AURR Comments] - Attempted to edit a reply that does not exist.'
            );
            return newResponse;
          }

          newResponse.pages[pageIndex].kh_replies[
            commentIndex
          ].kh_total_replies_count += 1;

          return newResponse;
        }
      );

      const placeholderComment = {
        kh_post_id: 'temporary-value',
        kh_post_time: new Date().toString(),
        kh_body: `${body}`,
        kh_author_user_id: userResponse?.kh_user_id || 'temporary-value',
        kh_author_user_login: userResponse?.kh_user_login || 'temporary-value',
        kh_author_avatar: userResponse?.kh_user_avatar || 'temporary-value',
        kh_kudos_count: 0,
        kh_total_replies_count: 0,
        kh_author_sso_id: sub || '',
      };

      queryClient.setQueryData<InfiniteData<KhorosRepliesGetSchema>>(
        [REPLIES_QUERY_KEY, { commentId }],
        old => {
          const placeholderPage = {
            kh_replies: [],
            status: 'success',
            statuscode: 200,
          };

          const newResponse = {
            pages: [...(old?.pages || [placeholderPage])],
            pageParams: old?.pageParams || [{ 0: undefined }],
          };

          const { pages } = newResponse;

          const flatPages = pages.flatMap(page => page.kh_replies);
          // Remove the oldest comment from view to make room for the new comment.
          if (pages.length > 1 || pages[0]?.kh_replies.length === pageSize) {
            flatPages.pop();
          }
          flatPages.unshift(placeholderComment);

          newResponse.pages = chunk(flatPages, pageSize).map((page, idx) => {
            return {
              ...newResponse.pages[idx],
              kh_replies: page,
            };
          });

          return newResponse;
        }
      );

      queryClient.setQueryData<KhorosUserSchema | undefined>(
        [USER_QUERY_KEY, { sub }],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            kh_messages_count: old.kh_messages_count + 1,
          };
        }
      );

      return { savedTopic, savedReplies, savedReply, savedUser };
    },
    onError: (err, newReply, context) => {
      queryClient.setQueryData(
        [TOPIC_QUERY_KEY, { topicId, sub }],
        context?.savedTopic
      );
      queryClient.setQueryData(
        [REPLIES_QUERY_KEY, { topicId }],
        context?.savedReplies
      );
      queryClient.setQueryData(
        [REPLIES_QUERY_KEY, { commentId }],
        context?.savedReply
      );
      queryClient.setQueryData([USER_QUERY_KEY, { sub }], context?.savedUser);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [REPLIES_QUERY_KEY, { topicId }],
      });
      queryClient.invalidateQueries({
        queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
      });
      queryClient.invalidateQueries({
        queryKey: [REPLIES_QUERY_KEY, { commentId }],
      });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, { sub }] });
    },
  });
};
